import { AgentRunEvent } from '@lucidtech/las-sdk-browser';
import { memo, useMemo } from 'react';

import { ArrowTriangleIcon } from '@/components/icons';
import { useAgentRunTree } from '@/hooks';
import { filterEventsFromLastRun } from '@/utils';

import { ExportEventTooltip } from './ExportEventTooltip';
import { PredictionEventTooltip } from './PredictionEventTooltip';
import { ReviewEventTooltip } from './ReviewEventTooltip';
import { TriggerEventTooltip } from './TriggerEventTooltip';

export type AgentRunEventsCellProps = {
  agentId: string;
  runId: string;
};

const IAgentRunEventsCell = ({ agentId, runId }: AgentRunEventsCellProps) => {
  const { triggerEvents, predictionEvents, reviewEvents, exportEvents } = useAgentRunTree({ agentId, runId });

  const arrow = <ArrowTriangleIcon rotation="right" className="size-2 text-gray-300" />;

  const {
    lastPredictionEvents: filteredPredictionEvents,
    lastReviewEvents: filteredReviewEvents,
    lastExportEvents: filteredExportEvents,
  } = useMemo(() => {
    return filterEventsFromLastRun({ predictionEvents, reviewEvents, exportEvents });
  }, [exportEvents, predictionEvents, reviewEvents]);

  return (
    <div className="gap-sm flex items-center">
      {triggerEvents.map((event: AgentRunEvent) => (
        <TriggerEventTooltip event={event} agentId={agentId} runId={runId} key={event.resourceId} />
      ))}
      {filteredPredictionEvents.length > 0 ? arrow : null}
      {filteredPredictionEvents.map((event: AgentRunEvent) => (
        <PredictionEventTooltip event={event} agentId={agentId} runId={runId} key={event.resourceId} />
      ))}
      {filteredReviewEvents.length > 0 ? arrow : null}
      {filteredReviewEvents.map((event: AgentRunEvent) => (
        <ReviewEventTooltip event={event} agentId={agentId} runId={runId} key={event.resourceId} />
      ))}
      {filteredExportEvents.length > 0 ? arrow : null}
      {filteredExportEvents.map((event: AgentRunEvent) => (
        <ExportEventTooltip event={event} agentId={agentId} runId={runId} key={event.resourceId} />
      ))}
    </div>
  );
};

export const AgentRunEventsCell = memo(IAgentRunEventsCell) as typeof IAgentRunEventsCell;
